// src/screens/TrainerProfile.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Zap, Play, X } from "lucide-react";
import { PrimaryButton, TertiaryButton } from "../components/Button";
import Navbar from "../components/Navbar";
import TabBar from "../components/TabBar";
import trainerImg from "../assets/trainer.png";
import trainerPlaceholder from "../assets/trainer-placeholder.png";
import { useAuth } from "../contexts/AuthContext";
import { getTrainerProfile, getTrainerBookings } from "../services/api";

/* ——— types ——— */
type Tab = "about" | "settings";
type SettingId = "earnings" | "payment" | "availability" | "logout";
type MediaItem =
  | { type: "video"; url: string; title?: string }
  | { type: "image"; src: string; title?: string };

/* ——— components ——— */
const TrainingPhotoItem: React.FC<{
  photoUrl: string;
  index: number;
  onClick: (url: string) => void;
  placeholder: string;
}> = ({ photoUrl, index, onClick, placeholder }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const isGif = photoUrl?.toLowerCase().endsWith('.gif');

  return (
    <div
      className={`relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg ${imageLoaded && !imageError ? 'cursor-pointer' : 'cursor-not-allowed opacity-75'
        }`}
      onClick={() => {
        // Only open full screen if image loaded successfully and not showing placeholder
        if (imageLoaded && !imageError && photoUrl) {
          onClick(photoUrl);
        }
      }}
    >
      {!imageError ? (
        <img
          src={photoUrl}
          alt={`Training photo ${index + 1}`}
          className="w-full h-full object-cover"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            console.error('Failed to load image:', photoUrl);
            setImageError(true);
            (e.target as HTMLImageElement).src = placeholder;
          }}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-100">
          <img
            src={placeholder}
            alt="Image not available"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <span className="text-white text-xs font-medium bg-black/50 px-2 py-1 rounded">
              Image unavailable
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const VideoThumbnail: React.FC<{
  videoUrl: string;
  index: number;
  onClick: (url: string) => void;
}> = ({ videoUrl, index, onClick }) => {
  return (
    <div
      className="relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg cursor-pointer"
      onClick={() => onClick(videoUrl)}
    >
      <video
        src={videoUrl}
        className="w-full h-full object-cover"
        preload="metadata"
        muted
        playsInline
        onError={(e) => {
          // Hide video on error and show fallback
          (e.target as HTMLVideoElement).style.display = 'none';
        }}
      />
      <div className="absolute inset-0 bg-black/20 flex items-center justify-center pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-black/50 flex items-center justify-center">
          <Play className="w-6 h-6 text-white ml-1" />
        </div>
      </div>
    </div>
  );
};

/* ——— helpers ——— */
const getYouTubeId = (url: string): string => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.replace("/", "");
    if (u.searchParams.get("v")) return u.searchParams.get("v") as string;
    const parts = u.pathname.split("/");
    const i = parts.indexOf("embed");
    if (i >= 0 && parts[i + 1]) return parts[i + 1];
  } catch { }
  return "";
};

const getInstagramId = (url: string): string => {
  try {
    const u = new URL(url);
    if (u.hostname.includes("instagram.com")) {
      const parts = u.pathname.split("/");
      const pIndex = parts.indexOf("p");
      const reelIndex = parts.indexOf("reel");
      if (pIndex >= 0 && parts[pIndex + 1]) return parts[pIndex + 1];
      if (reelIndex >= 0 && parts[reelIndex + 1]) return parts[reelIndex + 1];
    }
  } catch { }
  return "";
};

const isVideoUrl = (url: string): boolean => {
  if (!url) return false;
  // Check for video file extensions (ignore query params/fragments)
  const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv', '.m4v'];
  let pathToCheck = url;
  try {
    pathToCheck = new URL(url).pathname;
  } catch {
    pathToCheck = url.split('?')[0].split('#')[0];
  }
  const isVideoFile = videoExtensions.some(ext => pathToCheck.toLowerCase().endsWith(ext));
  // Check for video hosting platforms
  const isVideoPlatform = url.includes("youtube.com") || url.includes("youtu.be") || url.includes("instagram.com");
  return isVideoFile || isVideoPlatform;
};

const getVideoThumbnail = (url: string): string => {
  const youtubeId = getYouTubeId(url);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/maxresdefault.jpg`;
  }

  const instagramId = getInstagramId(url);
  if (instagramId) {
    // Instagram doesn't provide direct thumbnail URLs, so we'll use a placeholder
    return trainerPlaceholder;
  }

  return trainerPlaceholder;
};

/* ——— media from server ——— */

export default function TrainerProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();

  const [tab, setTab] = useState<Tab>("about");
  const [trainerProfile, setTrainerProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [fullScreenImageError, setFullScreenImageError] = useState(false);
  const [totalSessions, setTotalSessions] = useState<number>(0);

  /* fetch trainer profile */
  useEffect(() => {
    const fetchTrainerProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const profile = await getTrainerProfile(user.id);
        setTrainerProfile(profile);
        // Fetch real session count from bookings
        try {
          const bookingsData = await getTrainerBookings(user.id);
          const count = (bookingsData?.bookings || []).filter(
            (b: any) => b.status !== 'cancelled'
          ).length;
          setTotalSessions(count);
        } catch (bookingErr) {
          console.warn('Could not fetch booking count:', bookingErr);
        }
      } catch (err) {
        console.error("Error fetching trainer profile:", err);
        setError("Failed to load trainer profile");
      } finally {
        setLoading(false);
      }
    };
    fetchTrainerProfile();
  }, [user?.id, location.key]);

  const goSetting = (id: SettingId) => {
    if (id === "logout") {
      logout();
      navigate("/");
    } else if (id === "earnings") navigate("/trainer/earnings");
    else if (id === "payment") navigate("/trainer/payment-info");
    else if (id === "availability") navigate("/trainer/availability");
  };

  const handleMediaClick = (mediaUrl: string) => {
    if (isVideoUrl(mediaUrl)) {
      // For external video platforms, open in new tab
      if (mediaUrl.includes("youtube.com") || mediaUrl.includes("youtu.be") || mediaUrl.includes("instagram.com")) {
        window.open(mediaUrl, '_blank');
      } else {
        // Local video files - open in full screen modal with video player
        setFullScreenImage(mediaUrl);
      }
    } else {
      // Open image in full screen
      setFullScreenImage(mediaUrl);
    }
  };

  const closeFullScreen = () => {
    setFullScreenImage(null);
    setFullScreenImageError(false);
  };

  /* render */
  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-[100dvh] flex flex-col">
        {/* Header / hero */}
        <header className="relative w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)] text-white">
          <Navbar onBack={() => navigate("/trainer/dashboard")} />

          <div className="px-5 pb-5">
            <div className="relative">
              {/* right-side hero photo */}
              <img
                src={trainerProfile?.profilePic || trainerPlaceholder}
                alt="Trainer"
                className="absolute right-0 top-0 h-[176px] object-contain pointer-events-none select-none bottom-[0px] z-0"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = trainerPlaceholder;
                }}
              />

              {/* name + stats */}
              <div className="pt-3 pb-4 relative z-10">
                <div className="text-[#EB2726] italic font-extrabold uppercase text-[28px] leading-[1.05]">
                  {loading ? (
                    "Loading…"
                  ) : error ? (
                    "Error"
                  ) : (
                    (() => {
                      const fullName = trainerProfile?.name || "HELENA PADILLA";
                      const nameParts = fullName.toUpperCase().split(' ');
                      const firstName = nameParts[0] || '';
                      const lastName = nameParts.slice(1).join(' ') || '';

                      return (
                        <>
                          <div>{firstName}</div>
                          {lastName && <div>{lastName}</div>}
                        </>
                      );
                    })()
                  )}
                </div>

                <div className="mt-1 text-white text-[14px]">
                  <span className="font-extrabold">{totalSessions}</span>{" "}
                  total sessions
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-[#FFC800]" />
                  <div className="flex items-baseline gap-1">
                    <span
                      className="text-white font-extrabold text-[24px] leading-[34px]"
                    >
                      {trainerProfile?.creditsPerHour ?? 2}
                    </span>
                    <span
                      className="text-white font-medium text-[14px]"
                    >
                      credits per hour
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-2 relative z-20">
                <PrimaryButton
                  label="EDIT PROFILE"
                  onClick={() => navigate("/trainer/edit-profile")}
                  className="!w-full"
                />
              </div>
            </div>
          </div>
        </header>

        {/* TabBar */}
        <TabBar
          tabs={["ABOUT", "SETTINGS"]}
          active={tab === "about" ? 0 : 1}
          onChange={(index) => setTab(index === 0 ? "about" : "settings")}
        />

        {/* Content */}
        <main className="px-5 pt-4 pb-16">

          {tab === "settings" ? (
            <div className="space-y-4">
              <TertiaryButton
                label="EARNINGS"
                layout="split"
                onClick={() => goSetting("earnings")}
                className="!w-full"
              />
              {/* Phase 1 not needed */}
              {/* <TertiaryButton
                label="PAYMENT METHOD INFO"
                layout="split"
                onClick={() => goSetting("payment")}
                className="!w-full"
              /> */}
              <TertiaryButton
                label="AVAILABILITY"
                layout="split"
                onClick={() => goSetting("availability")}
                className="!w-full"
              />
              <TertiaryButton
                label="LOGOUT"
                layout="split"
                onClick={() => goSetting("logout")}
                className="!w-full"
              />
            </div>
          ) : (
            <div className="space-y-5">
              {/* about text */}
              <div className="bg-white border border-[#DEDEDE] p-4">
                <p className="text-[14px] text-[#2D2D2D] leading-6">
                  {loading
                    ? "Loading…"
                    : error
                      ? "Failed to load profile."
                      : trainerProfile?.aboutMe ||
                      "A countdown timer picker in iOS style. This picker shows a countdown duration with hour, minute and second segments. The picker has a maximum duration of 23 hours, 59 minutes and 59 seconds."}
                </p>
              </div>

              {/* age / gender */}
              <div className="bg-white border border-[#DEDEDE] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[12px] text-[#8A8A8A] mb-1">Age</div>
                    <div className="text-[14px] font-extrabold text-[#2D2D2D]">
                      {loading ? "…" : trainerProfile?.age ?? "20"} years
                    </div>
                  </div>
                  <div>
                    <div className="text-[12px] text-[#8A8A8A] mb-1">Gender</div>
                    <div className="text-[14px] font-extrabold text-[#2D2D2D]">
                      {loading ? "…" : trainerProfile?.sex ?? "Male"}
                    </div>
                  </div>
                </div>
              </div>

              {/* media from server */}
              <div className="space-y-3">
                {/* Show training photos */}
                {trainerProfile?.trainingPhotos?.map((photoUrl: string, i: number) => (
                  <TrainingPhotoItem
                    key={`photo-${i}`}
                    photoUrl={photoUrl}
                    index={i}
                    onClick={handleMediaClick}
                    placeholder={trainerPlaceholder}
                  />
                ))}

                {/* Show training videos */}
                {trainerProfile?.trainingVideoLinks?.map((videoUrl: string, i: number) => {
                  const isVideo = isVideoUrl(videoUrl);

                  // For local video files, use VideoThumbnail component
                  if (isVideo && !videoUrl.includes("youtube.com") && !videoUrl.includes("youtu.be") && !videoUrl.includes("instagram.com")) {
                    return (
                      <VideoThumbnail
                        key={`video-${i}`}
                        videoUrl={videoUrl}
                        index={i}
                        onClick={handleMediaClick}
                      />
                    );
                  }

                  // For external video links, use thumbnail from getVideoThumbnail
                  const thumbnail = isVideo ? getVideoThumbnail(videoUrl) : trainerPlaceholder;
                  return (
                    <div
                      key={`video-${i}`}
                      className="relative w-full h-[200px] bg-gray-200 overflow-hidden rounded-lg cursor-pointer"
                      onClick={() => handleMediaClick(videoUrl)}
                    >
                      <img
                        src={thumbnail}
                        alt={`Training video ${i + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = trainerPlaceholder;
                        }}
                      />
                      {isVideo && (
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                            <Play className="w-8 h-8 text-white ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Show message if no media */}
                {(!trainerProfile?.trainingPhotos?.length && !trainerProfile?.trainingVideoLinks?.length) && !loading && (
                  <div className="text-center text-gray-500 text-sm py-8">
                    No training media uploaded yet
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Full screen image modal */}
      {fullScreenImage && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center">
          <button
            onClick={closeFullScreen}
            className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center"
          >
            <X className="w-6 h-6 text-white" />
          </button>
          {isVideoUrl(fullScreenImage) ? (
            // Video player for video files
            <div className="w-full h-full flex items-center justify-center px-4">
              <video
                src={fullScreenImage}
                controls
                autoPlay
                className="max-w-full max-h-full"
                onError={() => {
                  console.error('Failed to load video in full screen:', fullScreenImage);
                  setFullScreenImageError(true);
                }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
          ) : !fullScreenImageError ? (
            <img
              src={fullScreenImage}
              alt="Full screen view"
              className="max-w-full max-h-full object-contain"
              onClick={closeFullScreen}
              onError={(e) => {
                // If image fails to load in full screen, show error state
                console.error('Failed to load image in full screen:', fullScreenImage);
                setFullScreenImageError(true);
              }}
            />
          ) : (
            <div className="text-white text-center px-8">
              <p className="text-lg mb-4">Failed to load media</p>
              <p className="text-sm text-gray-400 mb-4">The file may be corrupted or the file path is incorrect.</p>
              <button
                onClick={closeFullScreen}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Close
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
